'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import React, { useState, useEffect } from 'react';
import { MdErrorOutline } from 'react-icons/md';
import { Card, CardBody, CardTitle, Form, Row, Col, Label, FormGroup, Input, Alert } from 'reactstrap';
import CustomInput from './Input';
import checkInputValidity from '../utils/checkInputValidity';
import { logSentryError } from '../utils/logSentry';
import { uploadGdprForm } from '../api/gdpr';
import Cta from './CtaBtn';
import { Locale, SynergiejobsDictionary } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

type Props = {
  dictionary: SynergiejobsDictionary;
  locale: Locale;
};

function GdprForm({ dictionary, locale }: Props) {
  const { formatMessage } = getI18nUtils(dictionary);
  const [inputs, setInputs] = useState({
    firstName: {
      type: 'text',
      value: '',
      touched: false,
      valid: false,
      minLength: 2,
      order: 1,
      required: true,
      intlId: 'gdpr-form-firstName',
    },
    lastName: {
      type: 'text',
      value: '',
      touched: false,
      valid: false,
      minlength: 2,
      order: 2,
      required: true,
      intlId: 'gdpr-form-lastName',
    },
    email: {
      type: 'email',
      value: '',
      touched: false,
      valid: true,
      order: 3,
      required: true,
      intlId: 'gdpr-form-email',
    },
    birthDate: {
      type: 'date',
      value: '',
      touched: false,
      valid: true,
      order: 4,
      required: false,
      intlId: 'birthDate',
    },
    requestTypes: {
      type: 'radio',
      value: null,
      order: 5,
      required: true,
      valid: false,
      intlId: 'gdpr-form-requestTypes',
      labelSuffix: {
        text: 'gdpr-form-requestTypes-more-info',
        linkText: 'gdpr-form-requestTypes-more-info-link',
        linkHref: `/privacy`,
      },
      options: [
        // T240813-629 requested to make rightToErasure the first option
        'gdpr-form-rightToErasure',
        'gdpr-form-rightOfAccess',
        'gdpr-form-rightOfPortability',
        'gdpr-form-rightOfRectification',
        'gdpr-form-rightToLimitation',
      ],
    },
    requestTypeExplanation: {
      type: 'textarea',
      value: '',
      touched: false,
      valid: true,
      order: 6,
      required: false,
      intlId: 'gdpr-form-requestTypesExplanation',
      class: 'longText',
    },
    profileType: {
      type: 'radio',
      value: null,
      order: 7,
      required: true,
      valid: false,
      intlId: 'gdpr-form-profileType',
      options: ['gdpr-form-applicant', 'gdpr-form-clientOrSupplier', 'gdpr-form-worker', 'gdpr-form-other'],
    },
    profileTypeExplanation: {
      type: 'textarea',
      value: '',
      order: 8,
      required: false,
      touched: false,
      valid: true,
      intlId: 'gdpr-form-profileTypeExplanation',
    },
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const app = process.env.NEXT_PUBLIC_APP;

  async function submitHandler(e) {
    e.preventDefault();
    setSubmitting(true);
    const { firstName, lastName, email, birthDate, profileType, profileTypeExplanation, requestTypes, requestTypeExplanation } =
      inputs;
    const body = {
      firstName: firstName.value,
      lastName: lastName.value,
      email: email.value,
      birthDate: birthDate.value ? birthDate.value : null,
      requestType: requestTypes.value,
      requestTypeExplanation: requestTypeExplanation.value,
      profileType: profileType.value,
      profileTypeExplanation: profileTypeExplanation.value,
      language: locale,
      app: app || 'synergiejobs.be',
    };

    try {
      await uploadGdprForm(app, body);
      setCompleted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      logSentryError(error);
      const isClientError = error.response?.status < 500; // StatusCode 4xx
      if (isClientError) setErrorMessage(error.response.data.message);
      else setErrorMessage(formatMessage({ id: 'internal-server-error' }));
    }
    setSubmitting(false);
  }

  // check form validity for every inputType and terms
  function checkFormValidity() {
    const validForm = Object.keys(inputs).every((input) => inputs[input].valid) && termsAccepted;
    setFormIsValid(validForm);
  }

  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted, errorMessage]);

  function handleInputChange(event) {
    event.persist();
    setInputs((previousInputs) => {
      const { value, name } = event.target;
      const changedInput = { ...previousInputs[name] };
      changedInput.value = value;
      changedInput.touched = true;
      changedInput.valid = checkInputValidity(changedInput);

      if (name === 'requestTypes') {
        const explanationRequired =
          value === 'gdpr-form-rightOfRectification' ||
          value === 'gdpr-form-rightOfPortability' ||
          value === 'gdpr-form-rightToLimitation';
        return {
          ...previousInputs,
          [name]: changedInput,
          requestTypeExplanation: {
            ...previousInputs.requestTypeExplanation,
            required: explanationRequired,
            valid: !explanationRequired,
          },
        };
      }

      if (name === 'profileType') {
        const otherRequired = value === 'gdpr-form-other';
        return {
          ...previousInputs,
          [name]: changedInput,
          profileTypeExplanation: {
            ...previousInputs.profileTypeExplanation,
            required: otherRequired,
            valid: !otherRequired,
          },
        };
      }
      return { ...previousInputs, [name]: changedInput };
    });
  }

  return (
    <div id="gdprForm">
      <CardTitle>
        <h1>{formatMessage({ id: 'rightsRequest-title' })}</h1>
      </CardTitle>
      <Card className="mt-4">
        <CardBody>
          {!completed ? (
            <Form onSubmit={(e) => submitHandler(e)}>
              <fieldset disabled={submitting} aria-busy={submitting}>
                <Row className="mb-3">
                  {Object.keys(inputs)
                    // important!:  to avoid an issue with ios, sort the inputs
                    .sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1))
                    .filter((inputType) => {
                      if (inputType === 'requestTypeExplanation') {
                        return (
                          inputs.requestTypes.value === 'gdpr-form-rightOfRectification' ||
                          inputs.requestTypes.value === 'gdpr-form-rightOfPortability' ||
                          inputs.requestTypes.value === 'gdpr-form-rightToLimitation'
                        );
                      }
                      if (inputType === 'profileTypeExplanation') return inputs.profileType.value === 'gdpr-form-other';

                      return true;
                    })
                    .map((inputType) => (
                      <Col
                        md={inputs[inputType].type === 'radio' || inputs[inputType].type === 'textarea' ? 12 : 6}
                        xs="12"
                        key={`gdprInput-${inputType}`}
                      >
                        <CustomInput
                          dictionary={dictionary}
                          config={inputs[inputType]}
                          input={inputType}
                          inputChanged={(e) => handleInputChange(e)}
                          locale={locale}
                        />
                      </Col>
                    ))}
                </Row>
                <Row className="mb-3">
                  <Col xs={12}>
                    <FormGroup check inline>
                      <Input
                        type="checkbox"
                        id="terms"
                        name="terms"
                        onChange={() => {
                          setTermsAccepted(!termsAccepted);
                        }}
                        checked={termsAccepted}
                      />
                      <Label for="terms" check>
                        {formatMessage(
                          { id: 'accept-privacy-terms' },
                          {
                            privacyLink: (
                              <a href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'privacy_policy' })}
                              </a>
                            ),
                            termsLink: (
                              <a href={`/${locale}/termsandconditions`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'termsandconditions' })}
                              </a>
                            ),
                          }
                        )}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                {errorMessage && (
                  <Alert color="danger" className="d-flex align-items-center py-2 mb-3">
                    <MdErrorOutline className="d-inline-block me-2" size="16" color="#842029" />
                    <span>{errorMessage}</span>
                  </Alert>
                )}

                <Cta className="cta red noArrow" type="submit" disabled={!formIsValid}>
                  {formatMessage({ id: 'gdpr-form-submit' })}
                </Cta>
              </fieldset>
            </Form>
          ) : (
            <p>{formatMessage({ id: 'gdpr-form-thanks' })}</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default GdprForm;
